import { useMutation, useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { Button, Card, ErrorNote, Spinner } from "../components/primitives";
import { api } from "../lib/api";
import { money, priced } from "../lib/format";
import type { CreditEntry } from "../lib/types";

const KIND_LABEL: Record<CreditEntry["kind"], string> = {
  topup: "Payment",
  charge: "Engagement",
  refund: "Refund",
  grant: "Granted",
  adjustment: "Adjustment",
};

/**
 * Balance, top-up and statement.
 *
 * The success URL only *suggests* that a payment went through — the account is
 * credited by the signed webhook, which may land a moment later, or days later
 * for a direct debit. So arriving here with `?paid=1` says "we are waiting for
 * confirmation" rather than "you have the money", and the balance is polled
 * until it moves.
 */
export function Billing() {
  const [params] = useSearchParams();
  const justPaid = params.get("paid") === "1";

  const credit = useQuery({
    queryKey: ["credits"],
    queryFn: api.credits,
    refetchInterval: justPaid ? 3_000 : false,
  });
  const packages = useQuery({ queryKey: ["packages"], queryFn: api.billingPackages });

  const buy = useMutation({
    mutationFn: (packageId: string) => api.checkout(packageId),
    // A full-page navigation, not a fetch: Stripe hosts the payment page.
    onSuccess: ({ url }) => window.location.assign(url),
  });

  if (credit.isLoading || packages.isLoading) return <Spinner />;

  return (
    <div className="max-w-read space-y-4">
      <h1 className="text-lg font-semibold">Credit</h1>

      {justPaid && (
        <div className="card px-4 py-3 text-sm">
          <span className="font-medium text-ink">Thank you.</span>{" "}
          <span className="text-ink-2">
            Your payment is being confirmed. Card payments land in seconds;
            SEPA direct debit can take a few days. This page updates itself.
          </span>
        </div>
      )}
      {params.get("cancelled") === "1" && (
        <div className="card px-4 py-3 text-sm text-ink-2">
          Checkout was cancelled. Nothing has been charged.
        </div>
      )}

      <Card title="Balance">
        <div className="grid gap-4 p-4 sm:grid-cols-3">
          <Figure label="Available" value={money(credit.data!.available_usd)} strong />
          <Figure label="Reserved by live runs" value={money(credit.data!.held_usd)} />
          <Figure label="Total" value={money(credit.data!.balance_usd)} />
        </div>
        <p className="border-t border-line px-4 py-3 text-xs leading-relaxed text-muted">
          A run reserves its ceiling when it starts and is charged what it
          actually costs when it ends — the difference is released. A standard
          engagement on the default model reserves{" "}
          {money(credit.data!.reserves_usd.standard)}; a cheaper model reserves less.
        </p>
      </Card>

      <Card title="Buy credit">
        {!packages.data!.configured ? (
          <p className="px-4 py-3 text-sm text-ink-2">
            Payments are not configured on this server, so credit has to be
            granted by an administrator.
          </p>
        ) : (
          <div className="grid gap-3 p-4 sm:grid-cols-3">
            {packages.data!.packages.map((p) => (
              <div
                key={p.id}
                className="flex flex-col gap-2 rounded-lg border border-line p-4"
              >
                <div className="font-medium text-ink">{p.label}</div>
                <div className="font-mono text-lg tabular-nums text-ink">
                  {priced(p.price, p.currency)}
                </div>
                <div className="text-xs text-muted">
                  {money(p.credit_usd)} of engagement credit
                </div>
                <Button
                  className="mt-auto"
                  onClick={() => buy.mutate(p.id)}
                  disabled={buy.isPending}
                >
                  {buy.isPending ? "Opening…" : "Buy"}
                </Button>
              </div>
            ))}
          </div>
        )}
        {buy.error && <ErrorNote error={buy.error} />}
      </Card>

      <Card title="Statement">
        {credit.data!.history.length === 0 ? (
          <p className="px-4 py-3 text-sm text-muted">Nothing yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="label border-b border-line text-left">
                <th className="px-4 py-2 font-medium">When</th>
                <th className="px-4 py-2 font-medium">What</th>
                <th className="px-4 py-2 text-right font-medium">Amount</th>
              </tr>
            </thead>
            <tbody>
              {credit.data!.history.map((entry) => (
                <tr key={entry.id} className="border-b border-line last:border-0">
                  <td className="whitespace-nowrap px-4 py-2 text-xs text-muted">
                    {entry.created_at?.slice(0, 10) ?? "—"}
                  </td>
                  <td className="px-4 py-2">
                    <span className="text-ink">{KIND_LABEL[entry.kind]}</span>
                    {entry.note && (
                      <span className="ml-2 text-xs text-muted">{entry.note}</span>
                    )}
                  </td>
                  <td
                    className={`whitespace-nowrap px-4 py-2 text-right font-mono tabular-nums ${
                      entry.amount_usd < 0 ? "text-ink-2" : "text-ink"
                    }`}
                  >
                    {entry.amount_usd > 0 ? "+" : ""}
                    {money(entry.amount_usd)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function Figure({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div>
      <div className="label">
        {label}
      </div>
      <div
        className={`mt-1 font-mono tabular-nums ${
          strong ? "text-xl text-ink" : "text-base text-ink-2"
        }`}
      >
        {value}
      </div>
    </div>
  );
}
